import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MetaTags from "react-meta-tags";
import { Button, Card, CardBody, Col, Row, Spinner, Table } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import axios from "../api/axios";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  KIND_LABEL,
  RATING,
  fmtDate,
  fmtDateTime,
  fmtTime,
  money,
  parseAmount,
  textOf,
} from "../digi/PublicTurnover/turnoverFormat";

const JOBS_URL = "/v2/pre-moveout-inspections/vendor/jobs";

// Assignment status -> what the vendor reads, and the theme's soft badge for it.
const STATUS = {
  assigned: { label: "Awaiting your quote", badge: "badge-soft-warning" },
  quoted: { label: "Pending approval", badge: "badge-soft-info" },
  approved: { label: "Approved", badge: "badge-soft-success" },
  declined: { label: "Declined", badge: "badge-soft-danger" },
  cancelled: { label: "Cancelled", badge: "badge-soft-dark" },
};
const FILTERS = [
  ["all", "All"],
  ["assigned", "Awaiting your quote"],
  ["quoted", "Pending approval"],
  ["approved", "Approved"],
  ["declined", "Declined"],
  ["cancelled", "Cancelled"],
];
const TONE_BADGE = { success: "badge-soft-success", warning: "badge-soft-warning", danger: "badge-soft-danger" };
const COLUMNS = 8;

const hasValue = (v) => v !== undefined && v !== null && v !== "";
const ts = (v) => {
  const t = v ? new Date(v).getTime() : NaN;
  return Number.isFinite(t) ? t : null;
};
// Soonest move-out first (that is the deadline the vendor works to); undated jobs last;
// ties broken by the most recent assignment.
const byUrgency = (a, b) => {
  const ma = ts(a.moveout_date);
  const mb = ts(b.moveout_date);
  if (ma !== mb) {
    if (ma === null) return 1;
    if (mb === null) return -1;
    return ma - mb;
  }
  return (ts(b.assigned_at) || 0) - (ts(a.assigned_at) || 0);
};
const serverMessage = (err) => err && err.response && err.response.data && err.response.data.message;

const StatusBadge = ({ status }) => {
  const s = STATUS[status] || { label: status || "Unknown", badge: "badge-soft-dark" };
  return <span className={`badge ${s.badge}`}>{s.label}</span>;
};

const PriceCell = ({ job }) => {
  const q = job.quote || {};
  const a = job.approval || {};
  switch (job.status) {
    case "approved":
      return (
        <span className="text-success fw-semibold">Approved {money(hasValue(a.amount) ? a.amount : q.amount)}</span>
      );
    case "quoted":
      return <span className="fw-semibold">{money(q.amount)}</span>;
    case "declined":
      return hasValue(q.amount) ? (
        <span className="text-muted text-decoration-line-through">{money(q.amount)}</span>
      ) : (
        <span className="text-muted">&mdash;</span>
      );
    case "cancelled":
      return <span className="text-muted">{hasValue(q.amount) ? money(q.amount) : "—"}</span>;
    default:
      return <span className="text-muted">Quote needed</span>;
  }
};

const DetailLabel = ({ children }) => (
  <h6 className="text-uppercase text-muted fw-semibold mb-2" style={{ fontSize: "0.75rem", letterSpacing: "0.04em" }}>
    {children}
  </h6>
);

/** Inline estimate form for a job that is still waiting on the vendor. */
const QuoteForm = ({ job, onSent }) => {
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
    },
    []
  );

  const submit = async (e) => {
    e.preventDefault();
    const n = parseAmount(amount);
    if (n === null) {
      toast.error("Enter the amount you are quoting, in Canadian dollars.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${JOBS_URL}/${job.report_id}/${job.assignment_id}/quote`, { amount: n, notes: notes.trim() });
      toast.success(`Estimate of ${money(n)} sent for approval.`);
      await onSent();
    } catch (err) {
      toast.error(serverMessage(err) || "Could not send your estimate. Please try again.");
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const amountId = `tj-amount-${job.assignment_id}`;
  const notesId = `tj-notes-${job.assignment_id}`;
  return (
    <form onSubmit={submit} noValidate>
      <div className="mb-3">
        <label className="form-label" htmlFor={amountId}>
          Your estimate (CAD)
        </label>
        <div className="input-group">
          <span className="input-group-text">$</span>
          <input
            id={amountId}
            className="form-control"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
        </div>
      </div>
      <div className="mb-3">
        <label className="form-label" htmlFor={notesId}>
          Notes <span className="text-muted fw-normal">(optional)</span>
        </label>
        <textarea
          id={notesId}
          className="form-control"
          rows={3}
          placeholder="What the price covers, lead time, anything you need from the property"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
      <Button color="primary" type="submit" disabled={busy} className="d-inline-flex align-items-center gap-1">
        <FeatherIcon icon="send" size={15} />
        {busy ? "Sending…" : "Send estimate"}
      </Button>
    </form>
  );
};

/** Right-hand side of an expanded row: the form, or where the quote stands. */
const QuotePanel = ({ job, onSent }) => {
  const q = job.quote || {};
  const a = job.approval || {};
  const d = job.declined || {};
  const c = job.cancelled || {};

  switch (job.status) {
    case "assigned":
      return (
        <>
          <DetailLabel>Send your estimate</DetailLabel>
          <QuoteForm job={job} onSent={onSent} />
        </>
      );
    case "quoted":
      return (
        <>
          <DetailLabel>Your estimate</DetailLabel>
          <div className="fs-4 fw-semibold">{money(q.amount)}</div>
          {q.submitted_at ? <div className="text-muted small">Sent {fmtDateTime(q.submitted_at)}</div> : null}
          {q.notes ? (
            <p className="mt-2 mb-0" style={{ whiteSpace: "pre-wrap" }}>
              {q.notes}
            </p>
          ) : null}
          <p className="text-muted small mt-2 mb-0">Waiting for the property to approve.</p>
        </>
      );
    case "approved":
      return (
        <>
          <DetailLabel>Outcome</DetailLabel>
          <div className="fs-4 fw-semibold text-success">Approved {money(hasValue(a.amount) ? a.amount : q.amount)}</div>
          <div className="text-muted small">
            {a.by_name ? `By ${a.by_name}` : "Approved"}
            {a.at ? ` on ${fmtDateTime(a.at)}` : ""}
          </div>
          {hasValue(a.amount) && hasValue(q.amount) && Number(a.amount) !== Number(q.amount) ? (
            <div className="text-muted small">You quoted {money(q.amount)}.</div>
          ) : null}
          {a.note ? (
            <p className="mt-2 mb-0" style={{ whiteSpace: "pre-wrap" }}>
              {a.note}
            </p>
          ) : null}
        </>
      );
    case "declined":
      return (
        <>
          <DetailLabel>Outcome</DetailLabel>
          <div className="fw-semibold text-danger">Declined</div>
          <div className="text-muted small">
            {d.by_name ? `By ${d.by_name}` : ""}
            {d.at ? `${d.by_name ? " on " : ""}${fmtDateTime(d.at)}` : ""}
          </div>
          {d.reason ? (
            <p className="mt-2 mb-0" style={{ whiteSpace: "pre-wrap" }}>
              {d.reason}
            </p>
          ) : null}
          {hasValue(q.amount) ? <div className="text-muted small mt-2">You quoted {money(q.amount)}.</div> : null}
        </>
      );
    case "cancelled":
      return (
        <>
          <DetailLabel>Outcome</DetailLabel>
          <div className="fw-semibold text-muted">Cancelled by the property</div>
          {c.at ? <div className="text-muted small">{fmtDateTime(c.at)}</div> : null}
          {c.reason ? (
            <p className="mt-2 mb-0" style={{ whiteSpace: "pre-wrap" }}>
              {c.reason}
            </p>
          ) : null}
        </>
      );
    default:
      return null;
  }
};

const JobDetail = ({ job, onSent }) => {
  const repairs = Array.isArray(job.repairs) ? job.repairs : [];
  const rating = RATING[job.cleanliness_rating];
  return (
    <div className="p-3" style={{ background: "#f8f9fa" }}>
      <Row className="g-4">
        <Col lg={7}>
          <DetailLabel>Scope of work</DetailLabel>
          {job.scope ? (
            <p className="mb-3" style={{ whiteSpace: "pre-wrap", maxWidth: "72ch" }}>
              {job.scope}
            </p>
          ) : (
            <p className="text-muted mb-3">No scope written.</p>
          )}

          <DetailLabel>Repairs{repairs.length ? ` (${repairs.length})` : ""}</DetailLabel>
          {repairs.length ? (
            <ul className="mb-3 ps-3">
              {repairs.map((r, i) => (
                <li key={r._id || i} className="mb-1">
                  <span className="fw-semibold">{r.category || "Other"}</span>
                  {r.description ? <span className="text-muted"> &mdash; {r.description}</span> : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted mb-3">No repairs recorded.</p>
          )}

          <DetailLabel>Cleanliness</DetailLabel>
          {rating ? (
            <span className={`badge ${TONE_BADGE[rating.tone]}`}>{rating.label}</span>
          ) : (
            <span className="badge badge-soft-dark">Not rated</span>
          )}
          <p className="text-muted small mt-3 mb-0">
            Photos and videos of the suite are in the estimate request email you received.
          </p>
        </Col>
        <Col lg={5}>
          <div className="bg-white border rounded p-3 h-100">
            <QuotePanel job={job} onSent={onSent} />
          </div>
        </Col>
      </Row>
    </div>
  );
};

/** /turnover-jobs - every pre-moveout turnover assignment sent to the signed-in vendor. */
const TurnoverJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await axios.get(JOBS_URL);
      setJobs(Array.isArray(res && res.data && res.data.data) ? res.data.data : []);
    } catch (err) {
      setLoadError(serverMessage(err) || "Could not load your turnover jobs. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(
    () =>
      jobs.reduce(
        (acc, j) => {
          acc.all += 1;
          acc[j.status] = (acc[j.status] || 0) + 1;
          return acc;
        },
        { all: 0 }
      ),
    [jobs]
  );

  const visible = useMemo(() => {
    const list = filter === "all" ? jobs : jobs.filter((j) => j.status === filter);
    return [...list].sort(byUrgency);
  }, [jobs, filter]);

  const toggle = (id) => setOpenId((cur) => (cur === id ? null : id));
  const filterLabel = (FILTERS.find(([k]) => k === filter) || [])[1] || "";

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>Rentdigicare | Turnover Jobs</title>
        </MetaTags>
        <div className="container-fluid">
          <Breadcrumbs title="Building Operations" breadcrumbItem="Turnover Jobs" />
          <Row>
            <Col xl={12}>
              <Card>
                <CardBody>
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                    <h5 className="card-title mb-0">
                      Turnover Jobs <span className="text-muted fw-normal ms-2">({counts.all})</span>
                    </h5>
                    <Button
                      color="light"
                      size="sm"
                      onClick={load}
                      disabled={loading}
                      className="d-inline-flex align-items-center gap-1"
                    >
                      <FeatherIcon icon="refresh-cw" size={14} />
                      Refresh
                    </Button>
                  </div>

                  <div className="d-flex flex-wrap gap-2 mb-3">
                    {FILTERS.map(([key, label]) => {
                      const active = filter === key;
                      const n = counts[key] || 0;
                      return (
                        <button
                          key={key}
                          type="button"
                          className={`btn btn-sm rounded-pill ${active ? "btn-primary" : "btn-outline-secondary"}`}
                          onClick={() => setFilter(key)}
                          aria-pressed={active}
                        >
                          {label}
                          <span className={`ms-1 ${active ? "opacity-75" : "text-muted"}`}>{n}</span>
                        </button>
                      );
                    })}
                  </div>

                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="text-muted mt-2 mb-0">Loading your turnover jobs&hellip;</p>
                    </div>
                  ) : loadError ? (
                    <div
                      className="alert alert-danger d-flex flex-wrap align-items-center justify-content-between gap-2 mb-0"
                      role="alert"
                    >
                      <span>{loadError}</span>
                      <Button color="danger" outline size="sm" onClick={load}>
                        Try again
                      </Button>
                    </div>
                  ) : jobs.length === 0 ? (
                    <div className="text-center py-5">
                      <div className="text-muted mb-3">
                        <FeatherIcon icon="inbox" size={36} />
                      </div>
                      <h5 className="mb-1">No turnover jobs yet.</h5>
                      <p className="text-muted mb-0">
                        When a property assigns you a job you will get an email and it appears here.
                      </p>
                    </div>
                  ) : visible.length === 0 ? (
                    <div className="text-center py-5">
                      <p className="text-muted mb-2">No jobs are {filterLabel.toLowerCase()}.</p>
                      <Button color="link" size="sm" onClick={() => setFilter("all")}>
                        Show all jobs
                      </Button>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table hover className="align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Building</th>
                            <th>Suite</th>
                            <th>Move-out</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th>Price</th>
                            <th>Assigned</th>
                            <th style={{ width: 44 }} aria-label="Details" />
                          </tr>
                        </thead>
                        <tbody>
                          {visible.map((job) => {
                            const id = job.assignment_id;
                            const open = openId === id;
                            const sub = [job.building_name, textOf(job.company)].filter(Boolean).join(" · ");
                            return (
                              <React.Fragment key={id}>
                                <tr
                                  onClick={() => toggle(id)}
                                  style={{ cursor: "pointer" }}
                                  className={open ? "table-active" : ""}
                                  aria-expanded={open}
                                >
                                  <td>
                                    <div className="fw-semibold">{textOf(job.property) || "—"}</div>
                                    {sub ? <div className="text-muted small">{sub}</div> : null}
                                  </td>
                                  <td>{job.unit_number || "—"}</td>
                                  <td>
                                    <div>{fmtDate(job.moveout_date, { utc: true }) || "—"}</div>
                                    {job.moveout_time ? (
                                      <div className="text-muted small">{fmtTime(job.moveout_time)}</div>
                                    ) : null}
                                  </td>
                                  <td>{KIND_LABEL[job.kind] || job.kind || "—"}</td>
                                  <td>
                                    <StatusBadge status={job.status} />
                                  </td>
                                  <td>
                                    <PriceCell job={job} />
                                  </td>
                                  <td>{fmtDate(job.assigned_at) || "—"}</td>
                                  <td className="text-end text-muted">
                                    <FeatherIcon icon={open ? "chevron-up" : "chevron-down"} size={16} />
                                  </td>
                                </tr>
                                {open ? (
                                  <tr>
                                    <td colSpan={COLUMNS} className="p-0 border-bottom">
                                      <JobDetail job={job} onSent={load} />
                                    </td>
                                  </tr>
                                ) : null}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export { TurnoverJobs };
export default TurnoverJobs;
